// DownloadApp.jsx
import React, { useEffect, useRef } from "react";

const DownloadApp = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const elements = entry.target.querySelectorAll(
            '.animate-fade-in-left, .animate-fade-in-right, .animate-scale-in, .animate-fade-in-up'
          );
          
          if (entry.isIntersecting) {
            // Animate in when scrolling down
            elements.forEach((el) => {
              el.style.animationPlayState = 'running';
              el.style.webkitAnimationPlayState = 'running';
            });
          } else {
            // Reset when scrolling up/out of view
            elements.forEach((el) => {
              el.style.animation = 'none';
              el.style.webkitAnimation = 'none';
              el.style.opacity = '0';
              
              // Force reflow
              void el.offsetWidth;
              
              // Re-apply animation
              const animationClass = el.className.match(/animate-[\w-]+/)[0];
              const delay = el.style.animationDelay;
              
              if (animationClass === 'animate-fade-in-left') {
                el.style.animation = `fadeInLeft 0.8s ease-out forwards`;
                el.style.webkitAnimation = `fadeInLeft 0.8s ease-out forwards`;
              } else if (animationClass === 'animate-fade-in-right') {
                el.style.animation = `fadeInRight 0.8s ease-out forwards`;
                el.style.webkitAnimation = `fadeInRight 0.8s ease-out forwards`;
              } else if (animationClass === 'animate-scale-in') {
                el.style.animation = `scaleIn 0.8s ease-out forwards`;
                el.style.webkitAnimation = `scaleIn 0.8s ease-out forwards`;
              } else if (animationClass === 'animate-fade-in-up') {
                el.style.animation = `fadeInUp 0.8s ease-out forwards`;
                el.style.webkitAnimation = `fadeInUp 0.8s ease-out forwards`;
              }
              
              el.style.animationDelay = delay;
              el.style.webkitAnimationDelay = delay;
              el.style.animationPlayState = 'paused';
              el.style.webkitAnimationPlayState = 'paused';
            });
          }
        });
      },
      { 
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section className="download-section" id="download" ref={sectionRef}>
      <div className="download-container">
        {/* Section Header */}
        <div className="download-header">
          <span className="download-badge animate-fade-in-left" style={{ animationDelay: '0.2s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
            <span className="badge-icon">📱</span>
            Get the App
          </span>
          <h2 className="download-title animate-fade-in-left" style={{ animationDelay: '0.4s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
            Download FlyFi
            <span className="gradient-text"> Today</span>
          </h2>
          <p className="download-description animate-fade-in-left" style={{ animationDelay: '0.6s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
            Scan the QR code or click the buttons below to download our app
            and start your journey to the future.
          </p>
        </div>

        {/* QR Code Card */}
        <div className="qr-card-container animate-scale-in" style={{ animationDelay: '0.8s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
          <div className="qr-glass-card">
            <div className="qr-glow"></div>
            <div className="qr-code-wrapper">
            
                <img className="qr-code" src="qr-code.png" />
         
              <p className="qr-label">Scan to Download</p>
            </div>
            <div className="card-corner corner-tl"></div>
            <div className="card-corner corner-tr"></div>
            <div className="card-corner corner-bl"></div>
            <div className="card-corner corner-br"></div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="download-buttons">
          <button onClick={() => window.location.href = "https://flyfi.com.ng/api/get/app/device/download"} className="store-button app-store animate-fade-in-left" style={{ animationDelay: '1s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
            </svg>
            <div className="button-text">
              <span className="button-label">Download on the</span>
              <span className="button-store">App Store</span>
            </div>
          </button>

          <button onClick={() => window.location.href = "https://flyfi.com.ng/api/get/app/device/download"} className="store-button play-store animate-fade-in-right" style={{ animationDelay: '1.2s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            <div className="button-text">
              <span className="button-label">Get it on</span>
              <span className="button-store">Google Play</span>
            </div>
          </button>
        </div>

        <p className="download-info animate-fade-in-up" style={{ animationDelay: '1.4s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
          Available for iOS 12+ and Android 8.0+
        </p>
      </div>
    </section>
  );
};

export default DownloadApp;